import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateDeviceRequestSchema, type CreateDeviceRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import { useCreateDevice, type CreatedDeviceResponse } from "@/features/devices/hooks";

interface NewClpFormProps {
  onCreated: (result: CreatedDeviceResponse) => void;
  onCancel: () => void;
}

export function NewClpForm({ onCreated, onCancel }: NewClpFormProps) {
  const create = useCreateDevice();
  const form = useForm<CreateDeviceRequest>({
    resolver: zodResolver(CreateDeviceRequestSchema),
    defaultValues: { code: "", name: "" },
  });

  async function onSubmit(values: CreateDeviceRequest) {
    try {
      const result = await create.mutateAsync(values);
      toast.success("CLP criado — guarde a chave agora.");
      onCreated(result);
    } catch (err) {
      toast.error("Falha ao criar CLP.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  const errs = form.formState.errors;
  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3">
      <h2 className="text-[16px] font-semibold text-text-primary">Novo CLP</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="new-clp-code">Código</Label>
          <Input id="new-clp-code" {...form.register("code")} placeholder="EXT-01" />
          {errs.code && <p className="text-xs text-destructive">{errs.code.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new-clp-name">Nome</Label>
          <Input id="new-clp-name" {...form.register("name")} placeholder="Extrusora EXT-01" />
          {errs.name && <p className="text-xs text-destructive">{errs.name.message}</p>}
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? "Criando…" : "Criar CLP"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
